import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { getJokerFieldType } from '../utils/helper';
import AutoCompleteSelectUI from '../components/Interface/AutoCompleteSelectUI';
import FormErrorUI from '../components/Interface/FormErrorUI';
import { Field } from 'formik';
import { JokerFieldTypes } from '../config/joker';
import { DatePickerUI, NumberFormatInputUI, InputUI } from '../components/Interface';
import dayjs from 'dayjs';
import { formatCurrency } from '../utils/currencyHelper';

/**
 * Hook for generating joker columns, works from Formik context.
 * @param {Array} jokerLineFields - Joker field definitions
 * @param {string} key - Formik array key (e.g. 'productionItem')
 * @param {function} getValueIndex - Row index finder function
 * @returns {Array} DataGrid column definitions
 */
export default function useGenerateJokerCols(jokerLineFields, key, getValueIndex) {
  const { values, errors, setFieldValue, validateField } = useFormikContext();

  return useMemo(() => {
    return jokerLineFields.map((f) => {
      // Típus eldöntése: 'number', 'autocomplete', vagy 'string'
      const fieldType = getJokerFieldType(f);
      let columnType = 'string';
      if (fieldType === JokerFieldTypes.NUMBER) columnType = 'number';

      return {
        field: f.field,
        headerName: f.name,
        type: columnType,
        editable: true,
        sortable: false,
        valueGetter: (_data, row) => {
          const valueIndex = getValueIndex(values, row.id);
          if (fieldType === JokerFieldTypes.DATE) {
            return new Date(values?.[key]?.[valueIndex]?.[f.field]);
          }
          return values?.[key]?.[valueIndex]?.[f.field] ?? '';
        },
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          if (fieldType === JokerFieldTypes.SELECT && Array.isArray(f.valuesToForm)) {
            const findedItem = f.valuesToForm.find((u) => u.value === values?.[key]?.[valueIndex]?.[f.field]);
            return findedItem?.label ?? '';
          }
          if (fieldType === JokerFieldTypes.DATE) {
            const dateVal = values?.[key]?.[valueIndex]?.[f.field];
            return dateVal ? dayjs(dateVal).format('YYYY.MM.DD') : null;
          }
          if (fieldType === JokerFieldTypes.NUMBER) {
            const numVal = values?.[key]?.[valueIndex]?.[f.field];
            return numVal ? formatCurrency(numVal) : '';
          }

          return values?.[key]?.[valueIndex]?.[f.field] ?? '';
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          if (fieldType === JokerFieldTypes.SELECT && Array.isArray(f.valuesToForm)) {
            const findedItem = f.valuesToForm.find((u) => u.value === values?.[key]?.[valueIndex]?.[f.field]);
            return (
              <Field
                name={`${key}.${valueIndex}.${f.field}`}
                as={AutoCompleteSelectUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.[key]?.[valueIndex]?.[f.field]} />}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`${key}.${valueIndex}.${f.field}`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={f.valuesToForm}
                onBlur={async () => await validateField(`${key}.${valueIndex}.${f.field}`)}
              />
            );
          }
          if (fieldType === JokerFieldTypes.NUMBER) {
            return (
              <Field
                name={`${key}.${valueIndex}.${f.field}`}
                as={InputUI}
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.[key]?.[valueIndex]?.[f.field]} />}
                InputProps={{
                  inputComponent: NumberFormatInputUI,
                }}
                onChange={(e) => {
                  setFieldValue(`${key}.${valueIndex}.${f.field}`, e.target?.value);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target?.value,
                  });
                }}
                onBlur={async () => await validateField(`${key}.${valueIndex}.${f.field}`)}
              />
            );
          }
          if (fieldType === JokerFieldTypes.DATE) {
            return (
              <Field
                name={`${key}.${valueIndex}.${f.field}`}
                component={DatePickerUI}
                fullWidth
                variant="standard"
                value={dayjs(values?.[key]?.[valueIndex]?.[f.field])}
                onChange={(newDate) => {
                  const value = newDate?.isValid() ? dayjs(newDate).startOf('day').add(12, 'hour').toISOString() : null;
                  setFieldValue(`${key}.${valueIndex}.${f.field}`, value);
                }}
                helperText={<FormErrorUI message={errors?.[key]?.[valueIndex]?.[f.field]} />}
              />
            );
          }
          // Default: string mező
          return (
            <Field
              name={`${key}.${valueIndex}.${f.field}`}
              type="text"
              as={InputUI}
              variant="standard"
              fullWidth
              helperText={<FormErrorUI message={errors?.[key]?.[valueIndex]?.[f.field]} />}
              onChange={(e) => {
                setFieldValue(`${key}.${valueIndex}.${f.field}`, e.target?.value);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target?.value,
                });
              }}
              onBlur={async () => await validateField(`${key}.${valueIndex}.${f.field}`)}
            />
          );
        },
      };
    });
  }, [jokerLineFields, key, values, errors, setFieldValue, validateField, getValueIndex]);
}
